// PostHog telemetry shim for auth flows (DESIGN.md §0.6).
//
// PostHog stays no-op without a DSN per the Barel telemetry rule. We
// still emit events through the shim so when a project DSN lands, the code
// path is already wired and events will backfill.
//
// The actual PostHog SDK is owned by `@barel/telemetry`; in Wave 4.x it
// exports a module-level `track()` that no-ops. We wrap it with a tiny
// per-event helper so call-sites don't have to remember the event-name
// taxonomy.

import { track } from '@barel/telemetry';

export type AuthTelemetryEvent =
  | { name: 'auth_signup_submitted'; props: { locale: string } }
  | { name: 'auth_signup_succeeded'; props?: undefined }
  | { name: 'auth_signin_submitted'; props?: undefined }
  | {
      name: 'auth_signin_failed';
      props: { reason: 'bad_credentials' | 'rate_limited' | 'unverified' | 'unknown' };
    }
  | { name: 'auth_verify_resend_clicked'; props?: undefined }
  | { name: 'auth_verify_succeeded'; props?: undefined }
  | { name: 'auth_verify_failed'; props: { reason: 'expired' | 'invalid' | 'missing' | 'other' } }
  | { name: 'auth_forgot_submitted'; props?: undefined }
  | { name: 'auth_reset_submitted'; props?: undefined }
  | { name: 'auth_reset_succeeded'; props?: undefined };

/** Single emit helper — keeps event names + props centrally typed. */
export function emit<E extends AuthTelemetryEvent>(event: E): void {
  // `track` is a no-op when no DSN is configured. We still call it so the
  // shim collects breadcrumbs and so future configurations don't require
  // call-site changes.
  if (event.props !== undefined) {
    track(event.name, event.props as Record<string, unknown>);
  } else {
    track(event.name);
  }
}
